import i18next from 'i18next';
import React from 'react';

import { Label } from '@commons/utils/styledLibraryComponents';
import { numberToFixed } from '@commons/utils/format';
import DisplayNumber, { PRICE_NUMBER_DECIMALS } from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import theme from '@theme';

const CENTRAL_PRODUCT_BADGE_WIDTH = 'fit-content';

// numberToFixed drops the trailing decimal zeros, so a 20 rate reads "20%" and a 5.5 one "5,5%".
export const CATALOG_VAT_RATE_DECIMALS = 2;

const renderCellText = (value) => (
  <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
    {value || '-'}
  </Text>
);

// Category / sub-category fall back to "Aucune" when the product is not categorized.
const renderCategoryCell = (value) => (
  <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
    {value || i18next.t('GENERAL.NONE_VALUE')}
  </Text>
);

/**
 * Columns shared by the external-client product lists (catalog administration and the catalog
 * pickers of the creation / addition flows). ListView sorts on `propertyKey`, so they are named
 * after the sort keys accepted by POST /v2/b2b/external-clients/:id/catalog (an unknown key would
 * silently fall back to the product name) — the category filters key on `categoryId` /
 * `subCategoryId` instead and live in `getCatalogFilterList`.
 * The `Produit Centrale` column (isKitchen) is only relevant for clients owning central kitchens,
 * so it is omitted otherwise. The VAT column is opt-in: only the catalog administration displays it.
 *
 * @param {boolean} hasCentralKitchens - Whether the client owns at least one central kitchen
 * @param {Object} [options]
 * @param {boolean} [options.withVatRate=false] - Append the VAT rate column
 *
 * @returns {Array<Object>} The ListView columns
 */
export const getCatalogProductColumns = (hasCentralKitchens, { withVatRate = false } = {}) => {
  const columns = [
    {
      id: 'name',
      propertyKey: 'name',
      name: i18next.t('GENERAL.PRODUCT'),
      render: renderCellText,
    },
    {
      id: 'sku',
      propertyKey: 'sku',
      name: i18next.t('GENERAL.SKU'),
      render: renderCellText,
    },
    {
      id: 'category',
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
      render: renderCategoryCell,
    },
    {
      id: 'subCategory',
      propertyKey: 'subCategory',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
      render: renderCategoryCell,
    },
  ];

  if (hasCentralKitchens) {
    columns.push({
      id: 'isKitchen',
      propertyKey: 'isKitchen',
      name: i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_CENTRAL_PRODUCT'),
      render: (isKitchen) => (
        <Label
          background={theme.colors.greys.light}
          color={theme.colors.greys.darkest}
          type={'plain'}
          width={CENTRAL_PRODUCT_BADGE_WIDTH}
        >
          {isKitchen ? i18next.t('GENERAL.YES') : i18next.t('GENERAL.NO')}
        </Label>
      ),
    });
  }

  columns.push({
    id: 'priceWithTaxes',
    propertyKey: 'priceWithTaxes',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.MANUAL_COLUMN_PRICE'),
    render: (priceWithTaxes) => (
      <DisplayNumber
        customNumberDecimal={PRICE_NUMBER_DECIMALS}
        font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
        number={priceWithTaxes}
        displayCurrencyCode
      />
    ),
  });

  if (withVatRate) {
    columns.push({
      id: 'vatRate',
      propertyKey: 'vatRate',
      name: i18next.t('GENERAL.VAT_RATE'),
      tooltipText: i18next.t('EXTERNAL_CLIENTS.CATALOG.VAT_RATE_TOOLTIP'),
      render: (vatRate) =>
        renderCellText(numberToFixed(vatRate, CATALOG_VAT_RATE_DECIMALS, '-', '%')),
    });
  }

  return columns;
};
